import React from 'react';
import {
    ConsiderationDetail,
    ConsiderationLedgerRow,
    ConsiderationTermBase,
} from '../../../models/FlowOfFunds/flow-of-funds.model';
import { TypeOfConsideration } from '../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { formatMoney } from '../../../utils/flowOfFundsFormat';
import {
    CommonEquityRow,
    DeferredFixedPaymentAddRow,
    DeferredFixedPaymentRow,
    DeferredVariablePaymentAddRow,
    DeferredVariablePaymentRow,
    JvEquityRow,
    PreferredEquityRow,
} from './ConsiderationTermRows';

const TAIL_HEADERS = [
    'Admin Comment',
    'Buyer Comment',
    'Seller Comment',
    'Buyer Calculation Period, calendar days from payment timing',
    'Seller Calculation Response Period, calendar days',
    'Payment Date, calendar days from calculation agreement',
    'Put/Call Right',
    'Put/Call Explanation',
    'Buyer Approval',
    'Seller Approval',
];

const headersFor = (leading: string[], trailing: string[] = []) => [
    ...leading,
    ...TAIL_HEADERS,
    ...trailing,
];

const ACTION_HEADER = ['Actions'];

const DetailTable = ({
    title,
    headers,
    children,
}: {
    title: string;
    headers: string[];
    children: React.ReactNode;
}) => (
    <>
        <div className="ff-detail__group-title">{title}</div>
        <div className="ff-detail__scroll">
            <table className="ff-detail__table">
                <thead>
                    <tr>
                        {headers.map((header, index) => (
                            <th key={`${header}-${index}`}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>{children}</tbody>
            </table>
        </div>
    </>
);

const TotalRow = ({ leadingBlanks, total }: { leadingBlanks: number; total: number }) => (
    <tr className="ff-detail__total-row">
        <td>Total</td>
        {Array.from({ length: leadingBlanks }).map((_, index) => (
            <td key={index} />
        ))}
        <td>{formatMoney(total)}</td>
    </tr>
);

const DFP_LEADING_HEADERS = [
    '',
    'Payment Timing',
    'Amount',
    'Annualized Interest Accrual Rate',
    'Contingent?',
    'Contingency Dependent Variable (“ContDV”)',
    'ContDV minimum value',
    'Employment Contingency',
    'Tail Period for Employment',
];

const DVP_LEADING_HEADERS = [
    '',
    'Payment Timing',
    'Type',
    'Value at Closing',
    'Calculation Dependent Variable (“CalcDV”)',
    'CalcDV Baseline Value',
    'Calculation Multiple',
    'Maximum Payment',
    'Employment Contingency',
    'Tail period for Employment Contingency',
    'Penalty Multiple',
    'Distribution Periodicity',
];

interface ConsiderationDetailTablesProps {
    rows: ConsiderationLedgerRow[];
    detail: ConsiderationDetail;
    engagementId: number;
    viewerParty: TimelineParty | null;
    canAddRows: boolean;
}

const hasSummaryFaceValue = (
    rows: ConsiderationLedgerRow[],
    considerationType: TypeOfConsideration
): boolean => {
    const faceValue = rows.find((row) => row.considerationType === considerationType)?.faceValue;
    return faceValue != null && faceValue !== 0;
};

export const ConsiderationDetailTables = ({
    rows,
    detail,
    engagementId,
    viewerParty,
    canAddRows,
}: ConsiderationDetailTablesProps) => {
    const { deferredFixedPayments, deferredVariablePayments } = detail;
    const jv = detail.jvEquity;
    const common = detail.commonCorporateEquity;
    const preferred = detail.preferredCorporateEquity;

    const earnoutTerms = deferredVariablePayments.filter(
        (term) => term.considerationType === TypeOfConsideration.Earnout
    );
    const syntheticTerms = deferredVariablePayments.filter(
        (term) => term.considerationType === TypeOfConsideration.SyntheticJvEquity
    );

    const fixedTotal = deferredFixedPayments.reduce((sum, term) => sum + term.amount, 0);
    const earnoutTotal = earnoutTerms.reduce((sum, term) => sum + (term.valueAtClosing ?? 0), 0);
    const syntheticTotal = syntheticTerms.reduce(
        (sum, term) => sum + (term.valueAtClosing ?? 0),
        0
    );

    const showDeferredFixed = hasSummaryFaceValue(rows, TypeOfConsideration.DeferredFixedPayments);
    const showEarnout = hasSummaryFaceValue(rows, TypeOfConsideration.Earnout);
    const showSynthetic = hasSummaryFaceValue(rows, TypeOfConsideration.SyntheticJvEquity);
    const showDeferredVariableTitle = showEarnout || showSynthetic;
    const showJv = !!jv && hasSummaryFaceValue(rows, TypeOfConsideration.JvEquity);
    const showCommon = !!common && hasSummaryFaceValue(rows, TypeOfConsideration.CommonEquity);
    const showPreferred =
        !!preferred && hasSummaryFaceValue(rows, TypeOfConsideration.PreferredEquity);

    const hasAnyVisibleSection =
        showDeferredFixed || showEarnout || showSynthetic || showJv || showCommon || showPreferred;

    if (!hasAnyVisibleSection) {
        return null;
    }

    const hasRemovalAction = (terms: ConsiderationTermBase[]) =>
        terms.some((term) => term.canRequestRemoval);
    const showDeferredFixedActions = hasRemovalAction(deferredFixedPayments);
    const showEarnoutActions = hasRemovalAction(earnoutTerms);
    const showSyntheticActions = hasRemovalAction(syntheticTerms);
    const dfpHeaders = headersFor(
        DFP_LEADING_HEADERS,
        showDeferredFixedActions ? ACTION_HEADER : []
    );
    const earnoutHeaders = headersFor(DVP_LEADING_HEADERS, showEarnoutActions ? ACTION_HEADER : []);
    const syntheticHeaders = headersFor(
        DVP_LEADING_HEADERS,
        showSyntheticActions ? ACTION_HEADER : []
    );

    return (
        <div className="ff-detail">
            <h4 className="ff-detail__title">Consideration Detail</h4>

            {showDeferredFixed && (
                <DetailTable title="Deferred Fixed Payments" headers={dfpHeaders}>
                    {deferredFixedPayments.map((term, index) => (
                        <DeferredFixedPaymentRow
                            key={term.id}
                            term={term}
                            paymentNumber={index + 1}
                            engagementId={engagementId}
                            viewerParty={viewerParty}
                            showActions={showDeferredFixedActions}
                        />
                    ))}
                    <TotalRow leadingBlanks={1} total={fixedTotal} />
                    {canAddRows && (
                        <DeferredFixedPaymentAddRow
                            columnCount={dfpHeaders.length}
                            engagementId={engagementId}
                        />
                    )}
                </DetailTable>
            )}

            {showDeferredVariableTitle && (
                <div className="ff-detail__group-title">Deferred Variable Payments</div>
            )}

            {showEarnout && (
                <DetailTable title="Earnout" headers={earnoutHeaders}>
                    {earnoutTerms.map((term, index) => (
                        <DeferredVariablePaymentRow
                            key={term.id}
                            term={term}
                            paymentNumber={index + 1}
                            idPrefix="earnout"
                            engagementId={engagementId}
                            viewerParty={viewerParty}
                            showActions={showEarnoutActions}
                        />
                    ))}
                    <TotalRow leadingBlanks={2} total={earnoutTotal} />
                    {canAddRows && (
                        <DeferredVariablePaymentAddRow
                            columnCount={earnoutHeaders.length}
                            engagementId={engagementId}
                            considerationType={TypeOfConsideration.Earnout}
                        />
                    )}
                </DetailTable>
            )}

            {showSynthetic && (
                <DetailTable title="Synthetic JV Equity" headers={syntheticHeaders}>
                    {syntheticTerms.map((term, index) => (
                        <DeferredVariablePaymentRow
                            key={term.id}
                            term={term}
                            paymentNumber={index + 1}
                            idPrefix="synthetic"
                            engagementId={engagementId}
                            viewerParty={viewerParty}
                            showActions={showSyntheticActions}
                        />
                    ))}
                    <TotalRow leadingBlanks={2} total={syntheticTotal} />
                    {canAddRows && (
                        <DeferredVariablePaymentAddRow
                            columnCount={syntheticHeaders.length}
                            engagementId={engagementId}
                            considerationType={TypeOfConsideration.SyntheticJvEquity}
                        />
                    )}
                </DetailTable>
            )}

            {showJv && jv && (
                <DetailTable
                    title="JV Equity"
                    headers={headersFor([
                        'Value at Closing',
                        'Payment Timing',
                        'Calculation Dependent Variable (“CalcDV”)',
                        'CalcDV Baseline Value',
                        'Calculation Multiple',
                        'Employment Contingency',
                        'Tail period for Employment Contingency',
                        'Penalty Multiple',
                        'Distribution Periodicity',
                    ])}>
                    <JvEquityRow term={jv} engagementId={engagementId} viewerParty={viewerParty} />
                </DetailTable>
            )}

            {showCommon && common && (
                <DetailTable
                    title="Common Corp Equity"
                    headers={headersFor([
                        'Value at Closing',
                        'Expected Timing to Recap',
                        'Expected Payout, as multiple of current value',
                    ])}>
                    <CommonEquityRow
                        term={common}
                        engagementId={engagementId}
                        viewerParty={viewerParty}
                    />
                </DetailTable>
            )}

            {showPreferred && preferred && (
                <DetailTable
                    title="Preferred Corp Equity"
                    headers={headersFor([
                        'Value at Closing',
                        'Annual Dividend Rate',
                        'Dividend Payment Timing',
                        'Compounding Frequency',
                        'Expected Timing to Recap',
                        'Convertible into Common',
                        'Conversion Ratio',
                    ])}>
                    <PreferredEquityRow
                        term={preferred}
                        engagementId={engagementId}
                        viewerParty={viewerParty}
                    />
                </DetailTable>
            )}
        </div>
    );
};
